import {
  type AnyObject,
  mixed,
  type ObjectSchema,
  object,
  type StringSchema,
  string,
} from "yup";
import type { BabyQuote } from "@/api_client";
import { DateLimits } from "@/components/products/baby/utils/types";
import type { BabyEntryContent } from "@/services/cms/contentTypes/entry/BabyEntryContent";
import { getErrorMessageNew } from "@/utils/errorMessage";
import { AgeValidator } from "@/validators/shared/ageValidators";
import {
  isWithinDateRange,
  WithinDateRangeError,
} from "@/validators/shared/dateValidator";
import { FinnishSSN } from "@/validators/shared/ssnValidator";

type ValidationMessage = {
  Key?: string | null;
  Value?: string | null;
};

const getValidationMessage = (
  messages: (ValidationMessage | null)[] | null | undefined,
  errorKey: string,
) => {
  const defaultError = `Missing error for: ${errorKey}`;
  if (!messages) {
    return defaultError;
  }

  return messages.find((item) => item?.Key === errorKey)?.Value ?? defaultError;
};

const validateFemaleSSN = (val: string) => {
  const ssn = new FinnishSSN(val);
  return ssn.validateFemaleSSN();
};
const validateCorrectAge = (val: string) => {
  const ssn = new AgeValidator(val);
  return ssn.isAtMost45;
};

type BabyEntryFormValues = Pick<
  BabyQuote,
  | "productType"
  | "insuredParty"
  | "policyHolderSsn"
  | "babyMother"
  | "expectedDateOfBirth"
>;

const BabyMotherValidationSchema = (
  schema: StringSchema<string, AnyObject, undefined, "">,
  content?: BabyEntryContent | null,
) => {
  return schema
    .test(
      "wrong-ssn",
      getErrorMessageNew(
        content?.policyHolderSsn?.ValidationMessages,
        "wrong-female-ssn",
      ),
      validateFemaleSSN,
    )
    .test(
      "too-old",
      getErrorMessageNew(content?.babyMotherSsn?.ValidationMessages, "too-old"),
      validateCorrectAge,
    );
};

export const BabyEntryValidationSchema = (
  content?: BabyEntryContent | null,
): ObjectSchema<BabyEntryFormValues> =>
  object({
    productType: string().oneOf(["baby"]).required(),
    insuredParty: mixed<"Mother" | "PolicyHolder">()
      .required(
        getValidationMessage(
          content?.insuredParty?.ValidationMessages,
          "required",
        ),
      )
      .oneOf(["Mother", "PolicyHolder"]),
    policyHolderSsn: string()
      .required(
        getErrorMessageNew(
          content?.policyHolderSsn?.ValidationMessages,
          "required",
        ),
      )
      .when("insuredParty", {
        is: "Mother",
        then: (schema) =>
          schema.test(
            "wrong-ssn",
            getErrorMessageNew(
              content?.policyHolderSsn?.ValidationMessages,
              "wrong-ssn",
            ),
            (val) => {
              const ssn = new FinnishSSN(val);
              return ssn.validateSSN();
            },
          ),
        otherwise: (schema) => BabyMotherValidationSchema(schema, content),
      }),
    babyMother: object({ ssn: string() }).when(
      "insuredParty",
      (insuredParty: unknown, schema) => {
        const value = Array.isArray(insuredParty)
          ? insuredParty[0]
          : insuredParty;
        if (value === "Mother") {
          return schema.shape({
            ssn: BabyMotherValidationSchema(
              string()
                .required(
                  getErrorMessageNew(
                    content?.babyMotherSsn?.ValidationMessages,
                    "required",
                  ),
                )
                .test(
                  "duplicate-ssn",
                  getErrorMessageNew(
                    content?.babyMotherSsn?.ValidationMessages,
                    "duplicate-ssn",
                  ),
                  (value, ctx) => {
                    return (ctx.from ?? [])[1].value.policyHolderSsn !== value;
                  },
                ),
              content,
            ),
          });
        }
        return schema.shape({ ssn: string().notRequired() });
      },
    ),
    expectedDateOfBirth: string()
      .required(
        getErrorMessageNew(
          content?.expectedDateOfBirth?.ValidationMessages,
          "required",
        ),
      )
      .test("single-date-validation", "", (value, context) => {
        const dateError = isWithinDateRange(
          value,
          DateLimits.MIN,
          DateLimits.MAX,
        );
        switch (dateError) {
          case WithinDateRangeError.invalidDate:
            return context.createError({
              message: getErrorMessageNew(
                content?.expectedDateOfBirth?.ValidationMessages,
                "required",
              ),
            });
          case WithinDateRangeError.inThePast:
            return context.createError({
              message: getErrorMessageNew(
                content?.expectedDateOfBirth?.ValidationMessages,
                "is-not-future",
              ),
            });
          case WithinDateRangeError.tooSmall:
            return context.createError({
              message: getErrorMessageNew(
                content?.expectedDateOfBirth?.ValidationMessages,
                "less-then",
              ),
            });
          case WithinDateRangeError.tooLarge:
            return context.createError({
              message: getErrorMessageNew(
                content?.expectedDateOfBirth?.ValidationMessages,
                "more-then",
              ),
            });
        }
        return true;
      }),
  });
